import {
  CdkMenu,
  CdkMenuItem,
  CdkMenuItemCheckbox,
  CdkMenuTrigger,
} from '@angular/cdk/menu';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { ConnectorsActions } from '../../../connectors/connectors.actions';
import {
  ConnectorProvider,
  ConnectorView,
  mcpMarkFor,
} from '../../../connectors/connectors.models';
import { connectorsFeature } from '../../../connectors/connectors.reducer';
import { OPTION_ROW_CLASS } from '../../../connectors/connectors.styles';
import { LayoutActions } from '../../../layout/store/layout.actions';
import { AssistantsActions } from '../../assistants/store/assistants.actions';
import { assistantsFeature } from '../../assistants/store/assistants.reducer';
import { FileUploadTriggerDirective } from '../../shared/directives/file-upload-v2/file-upload-v2.directive';
import { FileUploadV2Store } from '../../shared/directives/file-upload-v2/file-upload-v2.store';
// By path: the actions files import nothing back, and the reducers are
// imported by path as `conversation.page.ts` does, so no barrel cycle.
import { TeammatesActions } from '../../teammates/store/teammates.actions';
import { teammatesFeature } from '../../teammates/store/teammates.reducer';

/** How many connector rows fit in the list's 220px box before it starts to scroll. */
const CONNECTOR_ROWS_BEFORE_SCROLL = 4;

/**
 * How old the Teammates and skills lists may be before opening "+" on a Teammate chat
 * reloads them. Each is about 261 KB, and the socket already reloads both on every (re)connect and
 * on `TeammatesUpdated` and `SkillsUpdated`, so a younger list is current. Only a change that no
 * notice announced can be missing from it, and it shows within this window. Every send is still
 * checked by the server.
 */
export const PLUS_MENU_LIST_MAX_AGE_MS = 30_000;

/**
 * The composer's "+" menu (SB-3711), one flat panel as designed in the Serious System:
 * attach a file, then a "Connectors" subheader with one row per connector this person has linked
 * (vendor mark, name, switch), then "Manage connectors" — which opens the Connectors settings.
 *
 * Only linked connectors appear here. Browsing and connecting is the Connectors settings' job, so the menu answers
 * "what can this conversation use?" rather than advertising a catalogue.
 *
 * Built on the CDK menu primitives `sd-dropdown-menu` uses rather than on that component, because its
 * option model has no subheader, no trailing switch and no vendor-mark icon. A CDK overlay is also
 * required rather than a plain child element: the composer's shell is `overflow-hidden` and would clip
 * a popover.
 *
 * The switches write the chat's exclusion set through `PATCH /v1/chats/:chatUuid/connectors`; before a
 * chat row exists they are inert and say why.
 */
@Component({
  selector: 'squadbox-prompt-input-plus-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkMenu,
    CdkMenuItem,
    CdkMenuItemCheckbox,
    CdkMenuTrigger,
    NgTemplateOutlet,
    TranslateModule,
    FileUploadTriggerDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseMcpIconDirective,
    fromSeriousSystem.ToggleComponent,
  ],
  template: `
    <button
      sdIconButton
      type="button"
      variant="icon"
      color="neutral"
      class="fade-in"
      [size]="isMobile() ? 'sm' : 'xs'"
      [cdkMenuTriggerFor]="plusMenu"
      (click)="refreshConnectors()"
      [attr.aria-label]="'PROMPT_INPUT.PLUS_MENU.OPEN' | translate"
    >
      <svg sdUseUIBasicIcon="plus"></svg>
    </button>

    <ng-template #plusMenu>
      <div
        cdkMenu
        role="menu"
        class="
          flex flex-col gap-0.5 p-2 w-80 min-w-70 max-w-80
          bg-shades-white border border-neutral-200 rounded-lg shadow-md
        "
      >
        <button
          cdkMenuItem
          type="button"
          squadboxFileUploadTrigger
          [class]="optionRowClass"
          [store]="fileUploadStore()"
          [accept]="fileUploadStore().acceptAttribute()"
          [multiple]="allowMultipleFiles()"
          [disabled]="fileUploadDisabled()"
        >
          <svg
            sdUseUIBasicIcon="attach"
            class="size-5 shrink-0 text-neutral-700"
            aria-hidden="true"
          ></svg>
          <span class="flex-1 truncate">
            {{ 'PROMPT_INPUT.PLUS_MENU.ATTACH_FILE' | translate }}
          </span>
        </button>

        <!--
          SB-3978 B4-T17, B7-T24: without the connector section the menu is
          "Add a file" alone: no divider, no connector rows
          (connectedConnectors is empty), no "Manage connectors". A Teammate
          chat shows it only where its turns can carry tokens.
        -->
        @if (connectorSectionVisible()) {
          <div class="p-2" aria-hidden="true">
            <div class="border-t border-neutral-200"></div>
          </div>
        }

        @if (connectedConnectors().length) {
          <!--
            Only the list scrolls, never the panel: "Add a file" and "Manage connectors" stay put
            however many connectors a person has linked (Figma: Composer - Dropdown, scroll variant).
          -->
          <div class="relative">
            <div
              class="flex flex-col gap-0.5 max-h-55 overflow-y-auto overflow-x-clip"
            >
              <!-- Subheader: 24px, caption semibold, 12px sides, 4px top and bottom. -->
              <div
                class="flex items-center h-6 shrink-0 px-3 py-1 typo-caption leading-4 font-semibold text-neutral-500"
                role="presentation"
              >
                {{ 'PROMPT_INPUT.PLUS_MENU.CONNECTORS' | translate }}
              </div>

              @for (
                connector of connectedConnectors();
                track connector.provider
              ) {
                @if (connector.status === 'connected') {
                  <!--
                    A menu checkbox, so the arrow keys reach it and Space flips it without closing the
                    menu (Enter flips and closes, as in any menu). The CDK also closes the menu on every
                    item click, which would shut the panel on each flick of a switch: the inner wrapper
                    takes the click first, stops it there, and triggers the item with keepOpen instead.

                    The switch only draws the item's state. "inert" keeps it out of the tab order and
                    the accessibility tree, so the row is the one control rather than a control nested
                    in another, and "[checked]" lands on the first render, so the switch no longer
                    paints off and slides on every time the menu opens.
                  -->
                  <div
                    cdkMenuItemCheckbox
                    #item="cdkMenuItemCheckbox"
                    [cdkMenuItemChecked]="isEnabled(connector.provider)"
                    [cdkMenuItemDisabled]="!chatUuid() || saving()"
                    (cdkMenuItemTriggered)="
                      handleToggle(
                        connector.provider,
                        !isEnabled(connector.provider)
                      )
                    "
                    [attr.aria-label]="connector.displayName"
                    [attr.aria-description]="pendingChatReason() || null"
                    class="flex h-10 shrink-0 rounded-lg hover:bg-neutral-100 focus-visible:bg-neutral-100 focus:outline-hidden"
                  >
                    <div
                      role="presentation"
                      class="flex flex-1 min-w-0 items-center gap-2 px-4 cursor-pointer"
                      (click)="
                        $event.stopPropagation();
                        item.trigger({ keepOpen: true })
                      "
                    >
                      <ng-container
                        *ngTemplateOutlet="
                          mark;
                          context: { $implicit: connector }
                        "
                      />
                      <sd-toggle
                        inert
                        class="flex-1 min-w-0"
                        size="sm"
                        labelVariant="option"
                        [label]="connector.displayName"
                        [checked]="item.checked"
                        [unavailable]="!chatUuid() || saving()"
                        [unavailableReason]="pendingChatReason()"
                      ></sd-toggle>
                    </div>
                  </div>
                } @else {
                  <!--
                    A link the provider stopped honouring. It stays in the menu — silently dropping a
                    connector someone linked reads as a bug — but it offers the repair, not a switch.
                  -->
                  <button
                    cdkMenuItem
                    type="button"
                    [class]="optionRowClass + ' shrink-0'"
                    (click)="handleClickConnect(connector.provider)"
                  >
                    <ng-container
                      *ngTemplateOutlet="
                        mark;
                        context: { $implicit: connector }
                      "
                    />
                    <span class="flex-1 truncate">{{
                      connector.displayName
                    }}</span>
                    <span
                      class="typo-caption font-semibold text-primary-500 shrink-0"
                    >
                      {{ 'PROMPT_INPUT.PLUS_MENU.RECONNECT' | translate }}
                    </span>
                  </button>
                }
              }
            </div>

            @if (isConnectorListScrollable()) {
              <div
                class="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-shades-white to-transparent pointer-events-none"
                aria-hidden="true"
              ></div>
            }
          </div>

          <div class="px-2 pb-2" aria-hidden="true">
            <div class="border-t border-neutral-200"></div>
          </div>
        }

        @if (connectorSectionVisible()) {
          <button
            cdkMenuItem
            type="button"
            [class]="optionRowClass"
            (click)="handleClickManage()"
          >
            <svg
              sdUseUIBasicIcon="settings"
              class="size-5 shrink-0 text-neutral-700"
              aria-hidden="true"
            ></svg>
            <span class="flex-1 truncate">
              {{ 'PROMPT_INPUT.PLUS_MENU.MANAGE' | translate }}
            </span>
          </button>
        }
      </div>
    </ng-template>

    <!-- The vendor mark from the catalog when the sprite ships it, else the generic connector icon. -->
    <ng-template #mark let-connector>
      @if (markOf(connector); as icon) {
        <svg
          [sdUseMcpIcon]="icon"
          class="size-5 shrink-0"
          aria-hidden="true"
        ></svg>
      } @else {
        <svg
          sdUseUIBasicIcon="share"
          class="size-5 shrink-0 text-neutral-700"
          aria-hidden="true"
        ></svg>
      }
    </ng-template>
  `,
})
export class PromptInputPlusMenuComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  protected readonly optionRowClass = OPTION_ROW_CLASS;

  /** The upload store the re-hosted `squadboxFileUploadTrigger` writes into. */
  public readonly fileUploadStore = input.required<FileUploadV2Store>();
  public readonly allowMultipleFiles = input<boolean>(false);
  public readonly fileUploadDisabled = input<boolean>(false);
  public readonly surface = input<string>('general');
  public readonly saving = input<boolean>(false);
  public readonly isMobile = input<boolean>(false);
  /**
   * `false`: the menu shows "Add a file" only and never loads connectors. On a
   * Teammate chat the composer sets it from the Teammate's skill traits
   * (SB-3978 B7-T24, decision (a)). Every other host keeps the default.
   */
  public readonly showConnectors = input<boolean>(true);
  /**
   * Set on a Teammate chat (SB-3978 B7-T24): the connector section shows only
   * when some loaded connector advertises the current surface, and opening the
   * menu reloads the Teammate's skills in case the browser's copy is stale.
   */
  public readonly requireAdvertisedSurface = input<boolean>(false);

  /** `null` until the conversation has a row, which is what makes the switches inert. */
  public readonly chatUuid = input<string | null>(null);
  public readonly disabledConnectorIds = input<readonly string[]>([]);

  /** Emits the complete new exclusion set, never a single change, so the write is idempotent. */
  public readonly disabledConnectorIdsChange = output<string[]>();

  private readonly connectors = this.store.selectSignal(
    connectorsFeature.selectConnectors,
  );
  private readonly connectorsLoaded = this.store.selectSignal(
    connectorsFeature.selectLoaded,
  );
  private readonly teammatesLoadedAt = this.store.selectSignal(
    teammatesFeature.selectLoadedAt,
  );
  private readonly teammatesLoading = this.store.selectSignal(
    teammatesFeature.selectIsLoading,
  );
  private readonly teammatesError = this.store.selectSignal(
    teammatesFeature.selectError,
  );
  private readonly assistantsLoadedAt = this.store.selectSignal(
    assistantsFeature.selectLoadedAt,
  );
  private readonly assistantsLoading = this.store.selectSignal(
    assistantsFeature.selectIsLoading,
  );
  private readonly assistantsReloadFailed = this.store.selectSignal(
    assistantsFeature.selectReloadFailed,
  );

  /**
   * Only connectors this person has actually linked. Discovering and connecting belongs to the
   * Connectors settings, so the composer lists what a conversation can use rather than a catalogue.
   * `needs_reconnect` counts as linked: it is theirs, it is just broken, and it offers the repair.
   */
  public readonly connectedConnectors = computed<ConnectorView[]>(() =>
    this.showConnectors()
      ? this.connectors().filter(
          (connector) =>
            connector.offered &&
            connector.status !== null &&
            connector.runtimeSurfaces?.includes(this.surface()),
        )
      : [],
  );

  /**
   * The divider and "Manage connectors". On a chat that requires it, only when
   * a loaded connector advertises the surface: a core-ai without `teammate`
   * binds nothing there, so offering to manage connectors would mislead.
   */
  protected readonly connectorSectionVisible = computed(
    () =>
      this.showConnectors() &&
      (!this.requireAdvertisedSurface() ||
        this.connectors().some((connector) =>
          connector.runtimeSurfaces?.includes(this.surface()),
        )),
  );

  /**
   * The fade belongs at the bottom of a list that runs past its box, and nowhere else — over a short
   * list it would wash out the last row. Derived from the count rather than measured: the box is
   * 220px, the subheader takes 24px plus its 2px gap, and each row is 40px plus 2px, so four rows fit.
   */
  protected readonly isConnectorListScrollable = computed(
    () => this.connectedConnectors().length > CONNECTOR_ROWS_BEFORE_SCROLL,
  );

  protected readonly pendingChatReason = computed(() =>
    this.chatUuid()
      ? ''
      : (this.translateService.instant(
          'PROMPT_INPUT.PLUS_MENU.PENDING_CHAT',
        ) as string),
  );

  /** Load on mount only if the connector list has not loaded. */
  public ngOnInit(): void {
    if (this.showConnectors() && !this.connectorsLoaded()) {
      this.store.dispatch(ConnectorsActions.loadConnectors());
    }
  }

  public refreshConnectors(): void {
    const reloadSkills = this.requireAdvertisedSurface();
    // The Teammate's skills may be stale, and `showConnectors` may read false
    // because of it, so these reloads sit outside its guard. A Teammate's
    // skills join both stores, the pair the socket reloads; each is reloaded
    // when it alone is stale.
    if (
      reloadSkills &&
      this.listIsStale(
        this.teammatesLoading(),
        this.teammatesLoadedAt(),
        this.teammatesError() !== null,
      )
    ) {
      this.store.dispatch(
        TeammatesActions.loadTeammates({
          locale: this.translateService.getCurrentLang() as 'en' | 'fr',
        }),
      );
    }
    if (
      reloadSkills &&
      this.listIsStale(
        this.assistantsLoading(),
        this.assistantsLoadedAt(),
        this.assistantsReloadFailed(),
      )
    ) {
      this.store.dispatch(AssistantsActions.loadAssistants());
    }
    // The reload may show the section, so its list must be there when it does.
    if (this.showConnectors() || (reloadSkills && !this.connectorsLoaded())) {
      this.store.dispatch(ConnectorsActions.loadConnectors());
    }
  }

  /**
   * One list is not loading, and never loaded, is older than PLUS_MENU_LIST_MAX_AGE_MS, or
   * failed its last reload (which keeps the last success's `loadedAt`, so age alone would skip
   * the retry). A load in flight in the other store does not count. A reconnect needs no flag of
   * its own: the socket's `connect` handler reloads both, which renews their `loadedAt`.
   */
  private listIsStale(
    loading: boolean,
    loadedAt: number | null,
    failed: boolean,
  ): boolean {
    if (loading) {
      return false;
    }
    return (
      failed ||
      loadedAt === null ||
      loadedAt < Date.now() - PLUS_MENU_LIST_MAX_AGE_MS
    );
  }

  /** The vendor mark for this connector; see `mcpMarkFor` for why it is derived rather than sent. */
  public markOf(
    connector: ConnectorView,
  ): fromSeriousSystem.McpIconsType | null {
    return mcpMarkFor(connector.provider);
  }

  public isEnabled(provider: ConnectorProvider): boolean {
    return !this.disabledConnectorIds().includes(provider);
  }

  public handleToggle(provider: ConnectorProvider, enabled: boolean): void {
    if (!this.chatUuid() || this.saving()) {
      return;
    }
    const next = new Set(this.disabledConnectorIds());
    if (enabled) {
      next.delete(provider);
    } else {
      next.add(provider);
    }
    this.disabledConnectorIdsChange.emit([...next]);
  }

  public handleClickConnect(provider: ConnectorProvider): void {
    this.store.dispatch(ConnectorsActions.connectProvider({ provider }));
  }

  public handleClickManage(): void {
    this.store.dispatch(
      LayoutActions.openSettings({
        source: 'prompt-input-plus-menu',
        pane: 'connectors',
      }),
    );
  }
}
